import React, { useState } from 'react';
import { SafetyGuide, QuizQuestion } from '../types';
import { ShieldAlert, HeartPulse, Activity, CheckCircle2, Award, Sparkles, Check, HelpCircle } from 'lucide-react';

interface SafetySectionProps {
  guides: SafetyGuide[];
  quizQuestions: QuizQuestion[];
}

export const SafetySection: React.FC<SafetySectionProps> = ({ guides, quizQuestions }) => {
  const [currentQuizIndex, setCurrentQuizIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<number[]>([]);
  const [quizSubmitted, setQuizSubmitted] = useState(false);

  const handleSelectOption = (optionIndex: number) => {
    const updated = [...selectedAnswers];
    updated[currentQuizIndex] = optionIndex;
    setSelectedAnswers(updated);
  };

  const handleNextQuestion = () => {
    if (currentQuizIndex < quizQuestions.length - 1) {
      setCurrentQuizIndex(currentQuizIndex + 1);
    } else {
      setQuizSubmitted(true);
    }
  };

  const handleResetQuiz = () => {
    setCurrentQuizIndex(0);
    setSelectedAnswers([]);
    setQuizSubmitted(false);
  };

  const calculateScore = () => {
    let correct = 0;
    quizQuestions.forEach((q, idx) => {
      if (selectedAnswers[idx] === q.correctIndex) {
        correct += 1;
      }
    });
    return Math.round((correct / quizQuestions.length) * 100);
  };

  const score = calculateScore();

  return (
    <section className="py-12 bg-slate-900 text-white min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
        
        {/* Banner Title */}
        <div className="text-center max-w-3xl mx-auto space-y-3">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-red-500/20 text-red-300 rounded-full text-xs font-bold border border-red-500/30">
            <ShieldAlert className="w-4 h-4 text-red-400" />
            <span>长顺县全民水上安全与防溺水科普专项</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight">
            安全游泳宣传与水上自救全书
          </h2>
          <p className="text-slate-400 text-xs sm:text-sm leading-relaxed">
            生命重于泰山！长顺县游泳协会普及教育部为您整理了最严谨的防溺水铁律、水下抽筋自救、心肺复苏（CPR）救援流程与在线考查。
          </p>
        </div>

        {/* Safety Golden Rules Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {guides.map((guide) => (
            <div
              key={guide.id}
              className="bg-slate-800/80 backdrop-blur-md rounded-2xl p-6 border border-slate-700 hover:border-sky-500/50 transition-all space-y-4 shadow-xl"
            >
              <div className="flex items-center gap-3 border-b border-slate-700/80 pb-4">
                <div className="w-10 h-10 rounded-xl bg-sky-500/20 text-sky-400 flex items-center justify-center shrink-0">
                  {guide.icon_name === 'ShieldAlert' && <ShieldAlert className="w-6 h-6 text-red-400" />}
                  {guide.icon_name === 'Activity' && <Activity className="w-6 h-6 text-amber-400" />}
                  {guide.icon_name === 'HeartPulse' && <HeartPulse className="w-6 h-6 text-emerald-400" />}
                  {guide.icon_name === 'CheckCircle2' && <CheckCircle2 className="w-6 h-6 text-sky-400" />}
                </div>
                <div>
                  <span className="text-[10px] font-bold text-sky-400 bg-sky-950 px-2 py-0.5 rounded border border-sky-800">
                    {guide.tag}
                  </span>
                  <h3 className="text-base font-bold text-white mt-1">
                    {guide.title}
                  </h3>
                </div>
              </div>

              <p className="text-xs text-slate-300 leading-relaxed">
                {guide.summary}
              </p>

              <div className="bg-slate-900/60 p-4 rounded-xl border border-slate-800 space-y-2">
                {guide.content.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-xs text-slate-200">
                    <span className="w-4 h-4 rounded-full bg-sky-500/20 text-sky-400 text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                      {idx + 1}
                    </span>
                    <span className="leading-normal">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Interactive Water Safety Quiz Widget */}
        <div className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-3xl p-6 sm:p-10 border border-sky-500/30 shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-sky-500/10 rounded-full blur-3xl pointer-events-none" />

          <div className="relative z-10 space-y-6 max-w-3xl mx-auto">
            <div className="flex items-center gap-2">
              <Award className="w-6 h-6 text-amber-400" />
              <h3 className="text-xl sm:text-2xl font-black text-white">
                长顺县水上安全与防溺水知识线上挑战
              </h3>
            </div>
            <p className="text-slate-300 text-xs">
              答对全套防溺水测试题，即可在线免费获取由“长顺县游泳协会”颁发的数字“水上安全卫士”荣誉认证证书。
            </p>

            {!quizSubmitted ? (
              <div className="bg-slate-900/80 p-6 sm:p-8 rounded-2xl border border-slate-700 space-y-6">
                
                {/* Progress bar */}
                <div className="flex items-center justify-between text-xs text-slate-400">
                  <span>问题 {currentQuizIndex + 1} / {quizQuestions.length}</span>
                  <div className="w-36 h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-sky-500 transition-all duration-300"
                      style={{ width: `${((currentQuizIndex + 1) / quizQuestions.length) * 100}%` }}
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="text-base font-bold text-white leading-snug">
                    {quizQuestions[currentQuizIndex].question}
                  </h4>

                  <div className="space-y-2.5">
                    {quizQuestions[currentQuizIndex].options.map((option, optIdx) => {
                      const isSelected = selectedAnswers[currentQuizIndex] === optIdx;
                      return (
                        <button
                          key={optIdx}
                          onClick={() => handleSelectOption(optIdx)}
                          className={`w-full p-3.5 rounded-xl text-xs text-left transition-all flex items-center justify-between border ${
                            isSelected
                              ? 'bg-sky-600/30 text-white border-sky-400 font-bold shadow-md'
                              : 'bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-700 hover:text-white'
                          }`}
                        >
                          <span>{String.fromCharCode(65 + optIdx)}. {option}</span>
                          {isSelected && <Check className="w-4 h-4 text-sky-400" />}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="pt-4 flex justify-between items-center border-t border-slate-800">
                  <span className="text-xs text-slate-500">
                    {selectedAnswers[currentQuizIndex] === undefined ? '请先勾选一个答案' : '点击下一步继续答题'}
                  </span>
                  <button
                    onClick={handleNextQuestion}
                    disabled={selectedAnswers[currentQuizIndex] === undefined}
                    className={`px-6 py-2.5 text-xs font-bold rounded-xl transition-all ${
                      selectedAnswers[currentQuizIndex] === undefined
                        ? 'bg-slate-800 text-slate-600 cursor-not-allowed'
                        : 'bg-sky-500 hover:bg-sky-400 text-slate-950 font-black shadow-lg shadow-sky-500/20'
                    }`}
                  >
                    {currentQuizIndex < quizQuestions.length - 1 ? '下一题' : '提交测试生成荣誉勋章'}
                  </button>
                </div>

              </div>
            ) : (
              /* Quiz Result Honor Badge */
              <div className="bg-slate-900 p-8 rounded-2xl border-2 border-amber-400/50 text-center space-y-6 shadow-2xl relative">
                <div className="inline-flex p-4 rounded-full bg-amber-400/20 border border-amber-400/40 text-amber-400">
                  <Sparkles className="w-10 h-10 animate-bounce" />
                </div>

                <div>
                  <span className="text-xs font-bold text-amber-300 bg-amber-950/80 px-3 py-1 rounded-full border border-amber-500/40">
                    荣誉认证: {score >= 80 ? '水上安全防范达人' : '防溺水宣传普及学员'}
                  </span>
                  <h4 className="text-3xl font-black text-white mt-3">
                    答题得分: <span className="text-sky-400">{score}</span> 分
                  </h4>
                  <p className="text-xs text-slate-300 mt-2">
                    {score >= 80
                      ? '恭喜您掌握了全部水上安全与自救知识！特此授予长顺县游泳协会数字防溺水特别徽章。'
                      : '您已了解大部分安全原则，请多复习防溺水六不准与CPR救护细节。'}
                  </p>
                </div>

                {/* Digital Honor Certificate Stamp */}
                <div className="bg-slate-950 p-6 rounded-xl border border-amber-500/30 max-w-md mx-auto space-y-2 text-left text-xs font-mono">
                  <div className="flex justify-between text-[11px] text-amber-400">
                    <span>长顺县游泳协会 宣发字[2026]第089号</span>
                    <span>状态: 校验有效</span>
                  </div>
                  <div className="text-slate-200 font-sans font-bold pt-1">
                    授予市民/选手: 崇尚安全的泳者
                  </div>
                  <div className="text-slate-400 text-[11px]">
                    已完成《长顺县青少年与市民水上自救与防溺水全套课程》考察。
                  </div>
                  <div className="pt-2 text-right text-[10px] text-sky-400 font-sans">
                    长顺县游泳协会防溺水普及委员会 • 签发
                  </div>
                </div>

                <div className="flex justify-center gap-3 pt-2">
                  <button
                    onClick={handleResetQuiz}
                    className="px-5 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold rounded-xl border border-slate-700"
                  >
                    重新测试
                  </button>
                  <button
                    onClick={() => alert('电子安全勋章凭证代码已保存至个人中心！')}
                    className="px-6 py-2.5 bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs font-black rounded-xl shadow-lg shadow-amber-500/20"
                  >
                    保存荣誉凭证
                  </button>
                </div>

              </div>
            )}

          </div>
        </div>

      </div>
    </section>
  );
};
